import { BACKEND_BASE_URL, API_BASE_URL } from '../config/api';
import { authService } from './authService';
import type { Product } from '../types/shop';

interface PodBooksApiResponse {
  ok?: boolean;
  success?: boolean;
  data?: unknown;
}

export interface PodBookListing {
  id: string;
  title: string;
  description: string;
  author: string;
  imageUrl: string;
  coverPreviewUrl: string | null;
  interiorPreviewUrl: string | null;
  price: number;
  printPrice: number;
  finalPrice: number | null;
  pageCount: number | null;
  size: string | null;
  coverType: string | null;
  pageColorType: string | null;
  podStatus: string;
  approvedByAdmin: boolean;
  isPhysicalAvailable: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  stock: number;
  product: Product;
}

export interface CreatePodBookInput {
  name: string;
  pageCount: number;
  size?: string;
  colorType?: string;
  coverType?: string;
  interiorPdf?: File | null;
  coverPdf?: File | null;
  interiorPdfUrl?: string;
  coverPdfUrl?: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const asBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no'].includes(normalized)) {
      return false;
    }
  }
  return null;
};

const buildHeaders = (): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: '*/*',
  };

  const token = authService.getToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const storedUser = authService.getStoredUser();
  if (storedUser?.id) {
    headers['x-user-id'] = storedUser.id;
  }
  if (storedUser?.role) {
    headers['x-user-role'] = storedUser.role === 'customer' ? 'buyer' : storedUser.role;
  }

  return headers;
};

const toErrorMessage = (payload: unknown, fallback: string): string => {
  const record = asRecord(payload);
  const dataRecord = asRecord(record.data);
  return (
    asString(record.message) ||
    asString(record.error) ||
    asString(dataRecord.message) ||
    asString(dataRecord.error) ||
    fallback
  );
};

const normalizeStatus = (value: string | null): string => {
  const normalized = (value ?? 'draft').trim().toLowerCase();
  return normalized || 'draft';
};

const toProduct = (listing: Omit<PodBookListing, 'product'>): Product => ({
  id: listing.id,
  nameKey: listing.title,
  descriptionKey: listing.description,
  author: listing.author,
  authorName: listing.author,
  summary: listing.description,
  categoryKey: 'POD Books',
  categoryId: 'pod-books',
  categories: [{ slug: 'pod-books', label: 'POD Books' }],
  audience: 'public',
  mediaType: 'book',
  contentUrl: listing.interiorPreviewUrl ?? undefined,
  bookUrl: listing.interiorPreviewUrl ?? undefined,
  coverUrl: listing.imageUrl,
  hasBookUrl: Boolean(listing.interiorPreviewUrl),
  price: listing.finalPrice ?? listing.printPrice ?? listing.price,
  printPrice: listing.printPrice,
  currency: 'RWF',
  imageUrl: listing.imageUrl,
  pdfUrl: listing.interiorPreviewUrl ?? undefined,
  isDigital: false,
  isPODAvailable: true,
  podProjectId: listing.id,
  rating: 0,
  reviewCount: 0,
  stock: listing.stock,
  reviews: [],
});

const mapPodBookRecord = (value: unknown): PodBookListing | null => {
  const record = asRecord(value);
  const id = asString(record.id) ?? asString(record._id);
  if (!id) {
    return null;
  }

  const title = asString(record.name) ?? asString(record.title) ?? 'Untitled POD Book';
  const description = asString(record.summary) ?? 'POD-ready print edition available through Haapa.';
  const author = asString(record.author) ?? 'Haapa POD Creator';
  const printPrice = asNumber(record.printPrice) ?? asNumber(record.finalPrice) ?? asNumber(record.price) ?? 0;
  const finalPrice = asNumber(record.finalPrice);
  const price = asNumber(record.price) ?? finalPrice ?? printPrice;
  const coverPreviewUrl = asString(record.coverPdfUrl) ?? asString(record.coverUrl);
  const imageUrl = asString(record.coverUrl) ?? coverPreviewUrl ?? '/vite.svg';

  const listingBase = {
    id,
    title,
    description,
    author,
    imageUrl,
    coverPreviewUrl,
    interiorPreviewUrl: asString(record.interiorPdfUrl) ?? asString(record.bookUrl),
    price,
    printPrice,
    finalPrice,
    pageCount: asNumber(record.pageCount),
    size: asString(record.size),
    coverType: asString(record.coverType),
    pageColorType: asString(record.pageColorType),
    podStatus: normalizeStatus(asString(record.podStatus) ?? asString(record.status)),
    approvedByAdmin: asBoolean(record.approvedByAdmin) ?? false,
    isPhysicalAvailable: asBoolean(record.isPhysicalAvailable) ?? false,
    createdAt: asString(record.createdAt),
    updatedAt: asString(record.updatedAt),
    stock: asNumber(record.bookCount) ?? 0,
  };

  return {
    ...listingBase,
    product: toProduct(listingBase),
  };
};

export const buildFallbackPodListing = (product: Product): PodBookListing => {
  const listingBase = {
    id: product.id,
    title: product.nameKey,
    description: product.descriptionKey,
    author: product.authorName ?? product.author ?? 'Haapa Author',
    imageUrl: product.imageUrl,
    coverPreviewUrl: product.coverUrl ?? product.imageUrl,
    interiorPreviewUrl: product.bookUrl ?? product.pdfUrl ?? product.contentUrl ?? null,
    price: product.price,
    printPrice: typeof product.printPrice === 'number' ? product.printPrice : Number((product.price * 1.55).toFixed(2)),
    finalPrice: null,
    pageCount: null,
    size: null,
    coverType: null,
    pageColorType: null,
    podStatus: 'published',
    approvedByAdmin: true,
    isPhysicalAvailable: true,
    createdAt: null,
    updatedAt: null,
    stock: product.stock,
  };

  return {
    ...listingBase,
    product: {
      ...product,
      printPrice: listingBase.printPrice,
      isPODAvailable: true,
    },
  };
};

class PodBooksService {
  private getCandidates(): string[] {
    const candidates = new Set<string>();
    candidates.add(`${API_BASE_URL}/pod/books`);
    candidates.add(`${BACKEND_BASE_URL}/api/pod/books`);
    candidates.add(`${BACKEND_BASE_URL}/pod/books`);
    return [...candidates];
  }

  private getBookDeleteCandidates(bookId: string): string[] {
    const candidates = new Set<string>();
    candidates.add(`${API_BASE_URL}/books/${encodeURIComponent(bookId)}`);
    candidates.add(`${BACKEND_BASE_URL}/api/books/${encodeURIComponent(bookId)}`);
    candidates.add(`${BACKEND_BASE_URL}/books/${encodeURIComponent(bookId)}`);
    return [...candidates];
  }

  async getPodBooks(): Promise<PodBookListing[]> {
    let lastError: Error | null = null;

    for (const endpoint of this.getCandidates()) {
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: { Accept: '*/*' },
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = (await response.json()) as PodBooksApiResponse | unknown;
        const responseRecord = asRecord(payload);
        const list = Array.isArray(responseRecord.data)
          ? responseRecord.data
          : Array.isArray(payload)
            ? payload
            : [];

        const normalized = list
          .map((entry) => mapPodBookRecord(entry))
          .filter((entry): entry is PodBookListing => Boolean(entry));

        return normalized;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unable to load POD books.');
      }
    }

    throw lastError ?? new Error('Unable to load POD books.');
  }

  async createPodBook(input: CreatePodBookInput): Promise<PodBookListing> {
    const formData = new FormData();
    formData.append('name', input.name.trim());
    formData.append('pageCount', String(input.pageCount));
    if (input.size?.trim()) {
      formData.append('size', input.size.trim());
    }
    if (input.colorType?.trim()) {
      formData.append('colorType', input.colorType.trim());
    }
    if (input.coverType?.trim()) {
      formData.append('coverType', input.coverType.trim());
    }
    if (input.interiorPdf) {
      formData.append('interiorPdf', input.interiorPdf);
    }
    if (input.coverPdf) {
      formData.append('coverPdf', input.coverPdf);
    }
    if (input.interiorPdfUrl?.trim()) {
      formData.append('interiorPdfUrl', input.interiorPdfUrl.trim());
    }
    if (input.coverPdfUrl?.trim()) {
      formData.append('coverPdfUrl', input.coverPdfUrl.trim());
    }

    let lastError: Error | null = null;

    for (const endpoint of this.getCandidates()) {
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: buildHeaders(),
          body: formData,
        });

        if (!response.ok) {
          const raw = await response.text();
          let payload: unknown = raw;
          try {
            payload = raw ? JSON.parse(raw) : null;
          } catch {
            payload = raw;
          }
          throw new Error(toErrorMessage(payload, `Request failed with status ${response.status}`));
        }

        const payload = (await response.json()) as PodBooksApiResponse | unknown;
        const responseRecord = asRecord(payload);
        const entry = mapPodBookRecord(responseRecord.data ?? payload);

        if (!entry) {
          throw new Error('POD book created but the response could not be parsed.');
        }

        return entry;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unable to create POD book.');
      }
    }

    throw lastError ?? new Error('Unable to create POD book.');
  }

  async approvePodBook(bookId: string): Promise<PodBookListing> {
    let lastError: Error | null = null;

    for (const endpoint of this.getCandidates()) {
      try {
        const response = await fetch(`${endpoint}/${encodeURIComponent(bookId)}/approve`, {
          method: 'PUT',
          headers: buildHeaders(),
        });

        if (!response.ok) {
          const raw = await response.text();
          let payload: unknown = raw;
          try {
            payload = raw ? JSON.parse(raw) : null;
          } catch {
            payload = raw;
          }
          throw new Error(toErrorMessage(payload, `Request failed with status ${response.status}`));
        }

        const payload = (await response.json()) as PodBooksApiResponse | unknown;
        const responseRecord = asRecord(payload);
        const dataRecord = asRecord(responseRecord.data ?? payload);
        const entry = mapPodBookRecord(dataRecord.book ?? dataRecord);

        if (!entry) {
          throw new Error('POD book approved but the response could not be parsed.');
        }

        return entry;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unable to approve POD book.');
      }
    }

    throw lastError ?? new Error('Unable to approve POD book.');
  }

  async deletePodBook(bookId: string): Promise<void> {
    let lastError: Error | null = null;

    for (const endpoint of this.getBookDeleteCandidates(bookId)) {
      try {
        const response = await fetch(endpoint, {
          method: 'DELETE',
          headers: buildHeaders(),
        });

        if (!response.ok) {
          const raw = await response.text();
          let payload: unknown = raw;
          try {
            payload = raw ? JSON.parse(raw) : null;
          } catch {
            payload = raw;
          }
          throw new Error(toErrorMessage(payload, `Request failed with status ${response.status}`));
        }

        return;
      } catch (error: unknown) {
        lastError = error instanceof Error ? error : new Error('Unable to delete POD book.');
      }
    }

    throw lastError ?? new Error('Unable to delete POD book.');
  }
}

export const podBooksService = new PodBooksService();
